import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, User, Users, DollarSign, ShieldCheck, Wallet, FileText, ExternalLink } from 'lucide-react';
import { applicants as api } from '@/lib/api';
import { toast } from 'sonner';

const money = (v) => `A$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ApplicantsPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState('');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [viewing, setViewing] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    api.list(q ? { q } : {}).then((d) => setItems(d.items || []))
      .catch(() => toast.error('Failed'))
      .finally(() => setLoading(false));
  }, [q]);
  React.useEffect(load, [load]);

  return (
    <div className="p-8" data-testid="applicants-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">Applicants</h1>
          <p className="mt-1 text-sm text-slate-500">One record per person, many matters. Trust balance, VEVO status and full matter history rolled up.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="applicant-new-btn">
          <Plus className="mr-2 h-4 w-4" /> New applicant
        </Button>
        <Button variant="outline" onClick={async () => {
          if (!window.confirm('Scan every matter without an applicant link, create applicants from unique emails, and link them?')) return;
          try {
            const r = await api.backfill();
            toast.success(`Backfilled: ${r.applicants_created} applicants created, ${r.matters_linked} matters linked${r.skipped_no_email ? `, ${r.skipped_no_email} skipped (no email)` : ''}`);
            load();
          } catch (e) { toast.error(e?.response?.data?.detail || 'Backfill failed'); }
        }} data-testid="applicant-backfill-btn">Auto-backfill from matters</Button>
      </div>

      <div className="mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, passport…" className="max-w-md" data-testid="applicants-search" />
      </div>

      {loading ? <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
       : items.length === 0 ? <Card><CardContent className="p-10 text-center text-sm text-slate-500"><Users className="mx-auto mb-2 h-10 w-10 text-slate-300" />No applicants yet.</CardContent></Card>
       : <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <Card key={a.id} className="cursor-pointer transition hover:border-emerald-300" onClick={() => setViewing(a.id)} data-testid={`applicant-card-${a.id}`}>
              <CardContent className="p-5">
                <div className="mb-2 flex items-start justify-between">
                  <div>
                    <div className="font-serif text-lg font-semibold text-[#1A3A5C]">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.email}</div>
                  </div>
                  <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">{a.matter_count} matter{a.matter_count === 1 ? '' : 's'}</Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  {a.dob && <div><span className="text-slate-400">DOB:</span> {a.dob}</div>}
                  {a.passport_number && <div><span className="text-slate-400">Passport:</span> {a.passport_number}</div>}
                  {a.phone && <div><span className="text-slate-400">Phone:</span> {a.phone}</div>}
                  {a.nationality && <div><span className="text-slate-400">Nationality:</span> {a.nationality}</div>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }

      <ApplicantFormDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} />
      <ApplicantDetailDialog applicantId={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function ApplicantFormDialog({ open, onClose, onSaved }) {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) setForm({ name: '', email: '' }); }, [open]);
  const submit = async () => {
    if (!form.name?.trim() || !form.email?.trim()) { toast.error('Name and email required'); return; }
    setBusy(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v && v !== '') payload[k] = v; });
      await api.create(payload);
      toast.success('Applicant created'); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  const fld = (k, label, type = 'text') => (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <Input type={type} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`applicant-form-${k}`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="applicant-form-dialog">
        <DialogHeader><DialogTitle>New applicant</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          {fld('name', 'Full name')}
          {fld('email', 'Email', 'email')}
          {fld('dob', 'Date of birth', 'date')}
          {fld('passport_number', 'Passport number')}
          {fld('passport_expiry', 'Passport expiry', 'date')}
          {fld('phone', 'Phone', 'tel')}
          {fld('second_email', 'Second email', 'email')}
          {fld('nationality', 'Nationality')}
          {fld('country_of_birth', 'Country of birth')}
          {fld('emergency_contact_name', 'Emergency contact name')}
          {fld('emergency_contact_phone', 'Emergency contact phone', 'tel')}
          {fld('emergency_contact_email', 'Emergency contact email', 'email')}
          {fld('emergency_contact_relationship', 'Relationship')}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="applicant-form-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplicantDetailDialog({ applicantId, onClose }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (!applicantId) return;
    setLoading(true);
    api.get(applicantId).then(setData).finally(() => setLoading(false));
  }, [applicantId]);
  return (
    <Dialog open={!!applicantId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="applicant-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="h-4 w-4" /> {data?.name || 'Applicant'}</DialogTitle></DialogHeader>
        {loading || !data ? <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
         : <div className="space-y-4">
             <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3 text-sm md:grid-cols-4">
               <KV k="Email" v={data.email} />
               <KV k="Phone" v={data.phone} />
               <KV k="DOB" v={data.dob} />
               <KV k="Passport" v={data.passport_number} />
               <KV k="Nationality" v={data.nationality} />
               <KV k="Country of birth" v={data.country_of_birth} />
               <KV k="Second email" v={data.second_email} />
               <KV k="Emergency" v={data.emergency_contact_name ? `${data.emergency_contact_name}${data.emergency_contact_phone ? ' · ' + data.emergency_contact_phone : ''}` : null} />
             </div>

             <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
               <Stat icon={FileText} label="Matters" value={data.summary?.matter_count || 0} sub={`${data.summary?.active_matter_count || 0} active`} testid="applicant-stat-matters" />
               <Stat icon={Wallet} label="Trust balance" value={money(data.summary?.trust_balance)} sub="Across all matters" testid="applicant-stat-trust" />
               <Stat icon={DollarSign} label="Billed" value={money(data.summary?.total_billed)} sub={`${money(data.summary?.total_paid)} paid`} testid="applicant-stat-billed" />
               <Stat icon={DollarSign} label="Outstanding" value={money(data.summary?.total_outstanding)} sub="Unpaid" testid="applicant-stat-outstanding" />
             </div>

             {data.matters?.length > 0 && (
               <div>
                 <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Matter history</div>
                 <div className="rounded-lg border border-slate-200" data-testid="applicant-matter-list">
                   {data.matters.map((m) => (
                     <Link key={m.id} to={`/app/matters/${m.id}`} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-sm hover:bg-slate-50 last:border-b-0" data-testid={`applicant-matter-${m.id}`}>
                       <div>
                         <span className="font-semibold text-[#2ECC9A]">{m.ref}</span>
                         <span className="ml-2 text-slate-600">Subclass {m.subclass}</span>
                         <span className="ml-2 text-xs text-slate-400">{m.created_at?.slice(0, 10)}</span>
                       </div>
                       <div className="flex items-center gap-2">
                         <Badge className="border border-slate-200 bg-slate-50 text-slate-700 text-xs">{m.stage}</Badge>
                         {m.trust_balance != null && <span className="text-xs text-slate-500">A${(m.trust_balance || 0).toFixed(0)}</span>}
                         <ExternalLink className="h-3 w-3 text-slate-400" />
                       </div>
                     </Link>
                   ))}
                 </div>
               </div>
             )}

             {data.vevo_checks?.length > 0 && (
               <div>
                 <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-slate-500"><ShieldCheck className="h-3 w-3" /> Recent VEVO checks</div>
                 <div className="rounded-lg border border-slate-200 p-3 text-xs" data-testid="applicant-vevo-list">
                   {data.vevo_checks.slice(0, 5).map((v) => (
                     <div key={v.id} className="border-b border-slate-50 py-1 last:border-b-0">
                       <span className="font-mono text-[#2ECC9A]">{v.matter_ref}</span>
                       <span className="ml-2 text-slate-600">{v.result?.visa_class}</span>
                       <span className="ml-2 text-slate-400">expiry {v.result?.expiry_date}</span>
                     </div>
                   ))}
                 </div>
               </div>
             )}

             {data.invoices?.length > 0 && (
               <div>
                 <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Invoices across all matters</div>
                 <div className="rounded-lg border border-slate-200" data-testid="applicant-invoice-list">
                   {data.invoices.slice(0, 20).map((i) => (
                     <div key={i.id} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-sm last:border-b-0" data-testid={`applicant-invoice-${i.id}`}>
                       <div>
                         <span className="font-semibold text-[#2ECC9A]">{i.matter_ref}</span>
                         <span className="ml-2 text-slate-700">{i.description || (i.invoice_number ? `#${i.invoice_number}` : '—')}</span>
                       </div>
                       <div className="flex items-center gap-3">
                         <span className="font-semibold text-[#1A3A5C]">{money(i.amount)}</span>
                         <Badge className={`text-xs border ${i.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>{i.status}</Badge>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             )}
           </div>
        }
      </DialogContent>
    </Dialog>
  );
}

const KV = ({ k, v }) => <div><div className="text-xs text-slate-400">{k}</div><div className="text-sm text-[#1A3A5C]">{v || '—'}</div></div>;
const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={testid}>
    <div className="flex items-center gap-1 text-xs uppercase text-slate-500"><Icon className="h-3 w-3" /> {label}</div>
    <div className="font-serif text-lg font-bold text-[#1A3A5C]">{value}</div>
    {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
  </div>
);
